import isValid from "date-fns/isValid";
import createDOMPurify from "dompurify";
import compose from "lodash/fp/compose";
import curry from "lodash/fp/curry";
import filter from "lodash/fp/filter";
import map from "lodash/fp/map";
import reduce from "lodash/fp/reduce";
import size from "lodash/fp/size";
import split from "lodash/fp/split";
import toLower from "lodash/fp/toLower";
import trim from "lodash/fp/trim";
import uniq from "lodash/fp/uniq";
import proxy from "proxy-states";
import React from "react";
import { Remarkable } from "remarkable";

import { ensureArray } from "@app/service/helpers";
import { mention, mention_hsh } from "@app/service/mdPlugins";
import {
  CONST_LIST_OF_MYWORK,
  CONST_LIST_SS_OF_MYWORK,
  CONST_LISTORIGIN_AVOCATI,
  CONST_LISTORIGIN_CTRDOSARE,
  CONST_LISTORIGIN_INVOICE,
  CONST_LISTORIGIN_PROFORMA,
  CONST_LSTORIGIN_CONTACTE,
  CONST_LSTORIGIN_CONTPERS_MD,
  CONST_LSTORIGIN_PERSOANE,
} from "@app/state/insider/constants";

/* ---------------------------- QUERY PARAMETERS ---------------------------- */

export const listParams = proxy({
  [CONST_LIST_SS_OF_MYWORK]: {
    start: null,
    end: null,
  },
  setWork: function (state: any, start: any, end: any) {
    state[CONST_LIST_SS_OF_MYWORK].start = start;
    state[CONST_LIST_SS_OF_MYWORK].end = end;
  },
  getWork: function (state: any) {
    return state[CONST_LIST_SS_OF_MYWORK];
  },
});

/* ------------------------ FIELD DEFINITION HELPERS ------------------------ */

const normalizeDate = (df: any) => {
  // this should do the trick:
  if (isValid(new Date(df))) {
    return new Date(df);
  }

  if (process.env.REACT_APP_MODE === "REACT") {
    return isValid(new Date(df)) ? new Date(df) : new Date(0);
  }
  if (!df) {
    return new Date(df);
  }
  const a = new Date(df);
  return isValid(a) ? a : new Date(0);
};

function normalizeDisplayValue(x: any) {
  return x ? normalizeDate(x) : "";
}

const calcDateCellValue = curry((field: string, dt: any) => {
  return normalizeDate(dt?.[field]);
});

const calcDateCellValueDisplay = curry((field: string, dt: any) => {
  return normalizeDisplayValue(dt?.[field]);
});

const customizeDateFieldHeaderFilterData = (options: any) => {
  options.dataSource.postProcess = function (results: any[]) {
    return compose(
      (x: any[]) => {
        const u = ensureArray(x);
        u.unshift({ key: 1970, text: "(Gol)", value: 1970 });
        return u;
      },
      filter((x: any) => x["key"] !== 1970)
    )(results);
  };
};

// we play it with css adding a classname to parent
// note: the code works  but there is a flickering effect in the grid
// the cause is form the grid itself, not from the code below
// anyway is more logical to hide the column or show it, not to truncate the text...
// e.g:  [##findme012323##]

const renderTruncatedTextHeaderWithButton = (data: any) => {
  return React.createElement(
    "div",
    { className: "dx-truncated-text-header" },
    data.column.caption,
    React.createElement(
      "button",
      {
        className: "dx-header-button",
        onClick: (e: any) => {
          e.stopPropagation();
          const truncated = data.cellElement.dataset.truncated;
          let a;
          if (truncated === "true" || truncated === true) {
            a = false;
            data.cellElement.setAttribute("data-truncated", false);
            document
              .querySelector("#anyGrid-LIST_CTRDOSARE_ORIGIN")
              ?.classList.add("dx-truncated-text");
          } else {
            a = true;
            data.cellElement.setAttribute("data-truncated", true);
            document
              .querySelector("#anyGrid-LIST_CTRDOSARE_ORIGIN")
              ?.classList.remove("dx-truncated-text");
          }
          e.target.classList.toggle("dx-header-button-active");
          e.target.textContent = a ? "text scurt" : "text lung";
        },
      },
      "text lung."
    )
  );
};

/* ---------------------------- FIELD DEFINITIONS --------------------------- */
const invisibleID = {
  dataType: "number",
  visible: false,
  showInColumnChooser: false,
};

export const fieldDefs = {
  [`_KEYOF_TABLE_${CONST_LSTORIGIN_CONTACTE}`]: ["C_ID"],
  [`_KEYOF_TABLE_${CONST_LSTORIGIN_PERSOANE}`]: ["PRS_ID"],
  [`_KEYOF_TABLE_${CONST_LSTORIGIN_CONTPERS_MD}`]: ["C_ID"],
  [`_KEYOF_TABLE_${CONST_LISTORIGIN_AVOCATI}`]: ["AMA_ID"],
  [`_KEYOF_TABLE_${CONST_LIST_OF_MYWORK}`]: ["MYW_ID"],
  [`_KEYOF_TABLE_${CONST_LISTORIGIN_CTRDOSARE}`]: ["CTR_ID"],
  [`_KEYOF_TABLE_${CONST_LISTORIGIN_PROFORMA}`]: ["FEID"],
  [`_KEYOF_TABLE_${CONST_LISTORIGIN_INVOICE}`]: ["FF_ID"],
  C_ID: { ...invisibleID },
  PRS_ID: { ...invisibleID },
  DT_INSERTED: {
    caption: "Adaugat la",
    dataType: "date",
    format: undefined,
    calculateCellValue: calcDateCellValue("DT_INSERTED"),
    calculateDisplayValue: calcDateCellValueDisplay("DT_INSERTED"),
    headerFilterDataSource: customizeDateFieldHeaderFilterData,
  },
  DT_UPDATED: {
    caption: "Modificat la",
    dataType: "date",
    format: undefined,
    calculateCellValue: calcDateCellValue("DT_UPDATED"),
    calculateDisplayValue: calcDateCellValueDisplay("DT_UPDATED"),
    headerFilterDataSource: customizeDateFieldHeaderFilterData,
  },
  AVO_INSERTED: { caption: "Adaugat de", dataType: "string" },
  AVO_UPDATED: { caption: "Modificat de", dataType: "string" },
  DENUMIRE: { caption: "Denumire", dataType: "string", visible: true },
  LEGAL_FORM: { caption: "Organizare", dataType: "string", visible: true },
  ARHIVAT: {
    caption: "Arhivat",
    dataType: "boolean",
    visible: true,
    calculateDisplayValue: (x: any) => !x,
    headerFilterDataSource: [
      { value: true, text: "Arhivat" },
      { value: false, text: "Activ" },
    ],
  },
  NUME: { caption: "Nume", dataType: "string", visible: true },
  PRENUME: { caption: "Prenume", dataType: "string", visible: true },
  ENTITATE_CONTACT: { caption: "Entitate", dataType: "string", visible: true },
  DEPARTAMENT: { caption: "Departament", dataType: "string", visible: true },
  LISTA_TELEFON: { caption: "Telefon", dataType: "string", visible: true },
  LISTA_ADRESA: { caption: "Adresa", dataType: "string", visible: true },
  LISTA_CORESPONDENTA: {
    caption: "Correspondenta",
    dataType: "string",
    visible: true,
  },
  LISTA_EMAIL: { caption: "Email", dataType: "string", visible: true },
  LISTA_CONEXIUNE: { caption: "Conexiune", dataType: "string", visible: true },
  /* 
  CTR_ID SMALLINT,
  CLIENT_ID SMALLINT,
  CLIENTUL VARCHAR(202),
  CONTRACT_DOSAR VARCHAR(255),
  SURSA_FACTURARE VARCHAR(7),
  ENTITATE VARCHAR(8),
  TIP_OBIECT VARCHAR(9),
  NR_CTR VARCHAR(250),
  NR_REG_GEN VARCHAR(20),
  NR_DOSAR VARCHAR(50),
  DATA_DELA DATE,
  DATA_LA DATE,
  EXPLICATII VARCHAR(5000),
  MOD_FACTURARE VARCHAR(36),
  ONORARIU DOUBLE PRECISION,
  MONEDA VARCHAR(3),
  STARE VARCHAR(7),
  AVOCAT_A_RESPONSABIL VARCHAR(101),
  RESPONSABIL_CTR VARCHAR(101),
  MANAGER_CTR VARCHAR(101),
  RESPONSABIL_CLIENT VARCHAR(101),
  ECHIPA VARCHAR(8000),
  TARA VARCHAR(200),
  LIMBA VARCHAR(255),
  PARENT_ID INTEGER,
  PARENT_NAME VARCHAR(255),
  INTRODUS_LA TIMESTAMP,
  INTRODUS_DE VARCHAR(101),
  MODIFICAT_LA TIMESTAMP,
  MODIFICAT_DE VARCHAR(101),
  ULTIMA_ACTIVITATE DATE,
  VAL_MED_FCT_AN NUMERIC(18, 0))
  */
  CTR_ID: { ...invisibleID },
  CLIENT_ID: { ...invisibleID },
  CLIENTUL: { caption: "Client", dataType: "string", visible: true },
  CONTRACT_DOSAR: { caption: "Contract", dataType: "string", visible: true },
  SURSA_FACTURARE: { caption: "Rol la facturare", dataType: "string" },
  ENTITATE: { caption: "Rol functional", dataType: "string" },
  TIP_OBIECT: { caption: "Practica", dataType: "string" },
  NR_CTR: { caption: "Nr. contract", dataType: "string" },
  NR_REG_GEN: { caption: "Nr. reg. general", dataType: "string" },
  NR_DOSAR: { caption: "Nr. dosar", dataType: "string" },
  DATA_DELA: {
    caption: "De la data",
    dataType: "date",
    format: undefined,
    calculateCellValue: calcDateCellValue("DATA_DELA"),
    calculateDisplayValue: calcDateCellValueDisplay("DATA_DELA"),
    headerFilterDataSource: customizeDateFieldHeaderFilterData,
  },
  DATA_LA: {
    caption: "La data",
    dataType: "date",
    format: undefined,
    calculateCellValue: calcDateCellValue("DATA_LA"),
    calculateDisplayValue: calcDateCellValueDisplay("DATA_LA"),
    headerFilterDataSource: customizeDateFieldHeaderFilterData,
  },
  EXPLICATII: { caption: "Explicatii", dataType: "string" },
  MOD_FACTURARE: { caption: "Mod facturare", dataType: "string" },
  ONORARIU: { caption: "Onorariu", dataType: "number" },
  MONEDA: { caption: "Moneda", dataType: "string" },
  LIMBA: { caption: "Limba", dataType: "string" },
  STARE: { caption: "Stare", dataType: "string" },
  AVOCAT_A_RESPONSABIL: {
    caption: "Avocat asoc. responsabil",
    dataType: "string",
  },
  RESPONSABIL_CTR: { caption: "Responsabil contract", dataType: "string" },
  MANAGER_CTR: { caption: "Manager contract", dataType: "string" },
  RESPONSABIL_CLIENT: { caption: "Responsabil client", dataType: "string" },
  ECHIPA: {
    caption: "Echipa",
    dataType: "string",
    cssClass: "dx-field-long-text",
    headerCellRender: renderTruncatedTextHeaderWithButton,
    visible: false,
  },
  TARA: { caption: "Tara", dataType: "string" },
  PARENT_ID: {
    caption: "ID Contract parinte",
    dataType: "number",
    visible: false,
  },
  PARENT_NAME: {
    caption: "Grupare pe client, contract ",
    dataType: "string",
    visible: false,
  },
  INTRODUS_LA: {
    caption: "Introdus la",
    dataType: "date",
    format: undefined,
    calculateCellValue: calcDateCellValue("INTRODUS_LA"),
    calculateDisplayValue: calcDateCellValueDisplay("INTRODUS_LA"),
    headerFilterDataSource: customizeDateFieldHeaderFilterData,
  },
  INTRODUS_DE: { caption: "Introdus de", dataType: "string" },
  MODIFICAT_LA: {
    caption: "Modificat la",
    dataType: "date",
    format: undefined,
    calculateCellValue: calcDateCellValue("MODIFICAT_LA"),
    calculateDisplayValue: calcDateCellValueDisplay("MODIFICAT_LA"),
    headerFilterDataSource: customizeDateFieldHeaderFilterData,
  },
  MODIFICAT_DE: { caption: "Modificat de", dataType: "string" },
  ULTIMA_ACTIVITATE: {
    caption: "Ultima activitate la",
    dataType: "date",
    format: undefined,
    calculateCellValue: calcDateCellValue("ULTIMA_ACTIVITATE"),
    calculateDisplayValue: calcDateCellValueDisplay("ULTIMA_ACTIVITATE"),
    headerFilterDataSource: customizeDateFieldHeaderFilterData,
  },
  VAL_MED_FCT_AN: {
    caption: "Medie facturata anual (EUR)",
    dataType: "number",
  },
  /*   
  MYW_ID INTEGER,
  AMA_ID INTEGER,
  C_ID INTEGER,
  CTR_ID INTEGER,
  DATA_MYW DATE,
  ACTIVITATE VARCHAR(5002),
  AVOCAT VARCHAR(101),
  CLIENT VARCHAR(202),
  CONTRACT VARCHAR(255),
  OBSERVATII VARCHAR(1000),
  ORELUCR VARCHAR(10),
  WK_ASMIN INTEGER,
  LAST_WRITE TIMESTAMP,
  WK_IS_EDITABLE INTEGER,
  WK_IS_SU_EDITABLE INTEGER,
  WK_ARHIVAT INTEGER,
  FE_ID INTEGER,
  FACTURABIL INTEGER) 
  */

  MYW_ID: { ...invisibleID },
  MYWID: { ...invisibleID },
  AMA_ID: { ...invisibleID },
  AMAID: { ...invisibleID },
  CID: { ...invisibleID },
  CTRID: { ...invisibleID },
  DATA_MYW: {
    caption: "Data",
    dataType: "date",
    format: undefined,
    width: 100,
    calculateCellValue: calcDateCellValue("DATA_MYW"),
    calculateDisplayValue: calcDateCellValueDisplay("DATA_MYW"),
    headerFilterDataSource: customizeDateFieldHeaderFilterData,
  },
  DATAMYW: {
    caption: "Data",
    dataType: "date",
    format: undefined,
    width: 100,
    calculateCellValue: calcDateCellValue("DATAMYW"),
    calculateDisplayValue: calcDateCellValueDisplay("DATAMYW"),
    headerFilterDataSource: customizeDateFieldHeaderFilterData,
  },
  // extract 2/5 from width except date and hours, other fields will share 3/5:
  ACTIVITATE: {
    caption: "Activitate",
    dataType: "string",
    width: "calc(((100% -200) / 5 ) * 2)",
  },
  AVOCAT: { caption: "Avocat", dataType: "string" },
  CLIENT: { caption: "Client", dataType: "string" },
  CONTRACT: { caption: "Contract", dataType: "string" },
  OBSERVATII: { caption: "Observatii", dataType: "string" },
  ORELUCR: {
    caption: "Ore lucrate",
    dataType: "string",
    width: 100,
    alignment: "right",
  },
  WK_ASMIN: {
    dataType: "number",
    visible: false,
    showInColumnChooser: false,
  },
  LAST_WRITE: {
    caption: "Ultima modificare",
    dataType: "date",
    visible: false,
    showInColumnChooser: true,
    calculateCellValue: calcDateCellValue("LAST_WRITE"),
    calculateDisplayValue: calcDateCellValueDisplay("LAST_WRITE"),
    headerFilterDataSource: customizeDateFieldHeaderFilterData,
  },
  WK_IS_EDITABLE: {
    caption: "Editabil",
    dataType: "boolean",
    visible: false,
    showInColumnChooser: true,
  },
  WK_IS_SU_EDITABLE: {
    caption: "Editabil superuser",
    dataType: "boolean",
    visible: false,
    showInColumnChooser: true,
  },
  WK_ARHIVAT: {
    caption: "Arhivat",
    dataType: "boolean",
    visible: false,
    width: 100,
    calculateDisplayValue: (x: any) => !x,
    headerFilterDataSource: [
      { value: true, text: "Arhivat" },
      { value: false, text: "Activ" },
    ],
  },
  FE_ID: {
    dataType: "number",
    visible: false,
    showInColumnChooser: false,
  },
  FACTURABIL: {
    caption: "Facturabil",
    dataType: "boolean",
    visible: false,
    showInColumnChooser: true,
  },
  /* 
  -- LE TRATEZI IN lxr_definitions.ts >>>
    CID INTEGER,
    CLIENT VARCHAR(255),
    FEID INTEGER,
    CTRDEN VARCHAR(255),
    NR_FCT VARCHAR(255),
    DATA_FCT DATE,
    --- NU SE INSUMEAZA PE ECRAN, POT FI DIF
    VLAFCT_FTVA_TXT VARCHAR(23),
    VALFCT_TVA_TXT VARCHAR(23),
    VALFCT_CUTVA_TXT VARCHAR(23),    
    VALINC_TXT VARCHAR(23), -- [IN MONEDA FACTURII]
    RESTINC_TXT VARCHAR(23),
    MONEDA_FACTURII VARCHAR(3),
    OSTARE VARCHAR(10),
    DATASCADENTA DATE,
    
    VALFARATVACON_TXT VARCHAR(23),
 	VALTVACON_TXT VARCHAR(23),
    VALFCON_TXT  VARCHAR(23),
    RESTICON_TXT VARCHAR(23),
    MONEDA_RAPORTULUI VARCHAR(3),  
    -- DIFERITE INFO
    TIP_FACTURA VARCHAR(50),
 	COORD_CTR VARCHAR(255),
    RESP_DOSAR VARCHAR(255),
 --<< lxr_definitions.ts
 
    --- CALCULELE DIN SPATE , AI NEVOIE
 	  VALFARATVACON NUMERIC(18, 2),
    VALTVACON NUMERIC(18, 2),
    VALFCON NUMERIC(18, 2),
    VALICON NUMERIC(18, 2),  -- [IN MONEDA COMUNA]
    RESTICON NUMERIC(18,2),
  
   --  
    VALFARATVA NUMERIC(18, 2),
    VALTVA NUMERIC(18, 2),
    VALFCT NUMERIC(18, 2),
    VALINC NUMERIC(18, 2),
    RESTINC NUMERIC(18,2),
  --- NU CRED CA SUNT NECESARE ASTEA
    TIP_FCT INTEGER,
    VALF_ONO_INIT NUMERIC(18, 2),
    VALF_CHELT_INIT NUMERIC(18, 2),
    VALF_TVA_INIT NUMERIC(18, 2),
    VALF_ONO NUMERIC(18, 2),
    VALF_CHELT NUMERIC(18, 2),
    VALF_TVA NUMERIC(18, 2),
    VALI_ONO_INIT NUMERIC(18, 2),
    VALI_CHELT_INIT NUMERIC(18, 2),
    VALI_TVA_INIT NUMERIC(18, 2),
    VALI_ONO NUMERIC(18, 2),
    VALI_CHELT NUMERIC(18, 2),
    VALI_TVA NUMERIC(18, 2),
    VALI_TOTAL NUMERIC(18, 2),
    VERIFICAT SMALLINT,
    COLOR SMALLINT,
    APROBARE_FACTURA INTEGER,
    DATA_TRIMITERE DATE,
    DATA_APROBARE DATE
  */
  //  CID : { ...invisibleID },
  FEID: { ...invisibleID },
  CTRDEN: { caption: "Contract", dataType: "string" },
  NR_FCT: {
    caption: "Nr. factura",
    dataType: "string",
    calculateSortValue: (x: any) => x?.["FEID"] || 0,
  },
  DATA_FCT: {
    caption: "Data factura",
    dataType: "date",
    format: undefined,
    width: 100,
    calculateCellValue: calcDateCellValue("DATA_FCT"),
    calculateDisplayValue: calcDateCellValueDisplay("DATA_FCT"),
    headerFilterDataSource: customizeDateFieldHeaderFilterData,
  },
  VLAFCT_FTVA_TXT: {
    caption: "Valoare fara TVA",
    dataType: "string",
    alignment: "right",
  },
  VALFCT_TVA_TXT: {
    caption: "Valoare TVA",
    dataType: "string",
    alignment: "right",
  },
  VALFCT_CUTVA_TXT: {
    caption: "Valoare cu TVA",
    dataType: "string",
    alignment: "right",
  },
  VALINC_TXT: {
    caption: "Valoare incasata",
    dataType: "string",
    alignment: "right",
  },
  RESTINC_TXT: {
    caption: "Rest de incasat",
    dataType: "string",
    alignment: "right",
  },
  OSTARE: { caption: "Stare", dataType: "string" },
  DATASCADENTA: {
    caption: "Data scadenta",
    dataType: "date",
    format: undefined,
    width: 100,
    calculateCellValue: calcDateCellValue("DATASCADENTA"),
    calculateDisplayValue: calcDateCellValueDisplay("DATASCADENTA"),
    headerFilterDataSource: customizeDateFieldHeaderFilterData,
  },

  VALFARATVACON_TXT: {
    caption: "Valoare fara TVA (conversie*)",
    dataType: "string",
    alignment: "right",
  },
  VALTVACON_TXT: {
    caption: "Valoare TVA (conversie*)",
    dataType: "string",
    alignment: "right",
  },
  VALFCON_TXT: {
    caption: "Valoare cu TVA (conversie*)",
    dataType: "string",
    alignment: "right",
  },
  VALICON_TXT: {
    caption: "Valoare incasata (conversie*)",
    dataType: "string",
    alignment: "right",
  },
  RESTICON_TXT: {
    caption: "Rest de incasat (conversie*)",
    dataType: "string",
    alignment: "right",
  },
  TIP_FACTURA: { caption: "Tip factura", dataType: "string" },
  COORD_CTR: { caption: "Coordonator contract", dataType: "string" },
  RESP_DOSAR: { caption: "Responsabil dosar", dataType: "string" },
  VALFARATVA: {
    caption: "Valoare fara TVA (numeric)",
    dataType: "number",
    visible: false,
    showInColumnChooser: false,
  },
  VALTVA: {
    caption: "Valoare TVA (numeric)",
    dataType: "number",
    visible: false,
    showInColumnChooser: false,
  },
  VALFCT: {
    caption: "Valoare cu TVA (numeric)",
    dataType: "number",
    visible: false,
    showInColumnChooser: false,
  },
  VALINC: {
    caption: "Valoare incasata (numeric)",
    dataType: "number",
    visible: false,
    showInColumnChooser: false,
  },
  RESTINC: {
    caption: "Rest de incasat (numeric)",
    dataType: "number",
    visible: false,
    showInColumnChooser: false,
  },
  MONEDA_FACTURII: {
    caption: "Moneda facturii",
    dataType: "string",
    visible: false,
  },
  VALFARATVACON: {
    caption: "Valoare fara TVA (conversie, numeric)",
    dataType: "number",
    visible: false,
    showInColumnChooser: false,
  },
  VALTVACON: {
    caption: "Valoare TVA (conversie, numeric)",
    dataType: "number",
    visible: false,
    showInColumnChooser: false,
  },
  VALFCON: {
    caption: "Valoare cu TVA (conversie, numeric)",
    dataType: "number",
    visible: false,
    showInColumnChooser: false,
  },
  VALICON: {
    caption: "Valoare incasata (conversie, numeric)",
    dataType: "number",
    visible: false,
    showInColumnChooser: false,
  },
  RESTICON: {
    caption: "Rest de incasat (conversie, numeric)",
    dataType: "number",
    visible: false,
    showInColumnChooser: false,
  },
  MONEDA_RAPORTULUI: {
    caption: "*Moneda raportului (curs. dt. fact.)",
    dataType: "string",
    visible: false,
  },
  TIP_FCT: { ...invisibleID },
  VALF_ONO_INIT: { ...invisibleID },
  VALF_CHELT_INIT: { ...invisibleID },
  VALF_TVA_INIT: { ...invisibleID },
  VALF_ONO: { ...invisibleID },
  VALF_CHELT: { ...invisibleID },
  VALF_TVA: { ...invisibleID },
  VALI_ONO_INIT: { ...invisibleID },
  VALI_CHELT_INIT: { ...invisibleID },
  VALI_TVA_INIT: { ...invisibleID },
  VALI_ONO: { ...invisibleID },
  VALI_CHELT: { ...invisibleID },
  VALI_TVA: { ...invisibleID },
  VALI_TOTAL: { ...invisibleID },
  VERIFICAT: { ...invisibleID },
  COLOR: { ...invisibleID },
  APROBARE_FACTURA: { ...invisibleID },
  DATA_TRIMITERE: {
    caption: "Data trimitere",
    dataType: "date",
    format: undefined,
    width: 100,
    calculateCellValue: calcDateCellValue("DATA_TRIMITERE"),
    calculateDisplayValue: calcDateCellValueDisplay("DATA_TRIMITERE"),
    headerFilterDataSource: customizeDateFieldHeaderFilterData,
    visible: false,
  },
  DATA_APROBARE: {
    caption: "Data aprobare",
    dataType: "date",
    format: undefined,
    width: 100,
    calculateCellValue: calcDateCellValue("DATA_APROBARE"),
    calculateDisplayValue: calcDateCellValueDisplay("DATA_APROBARE"),
    headerFilterDataSource: customizeDateFieldHeaderFilterData,
    visible: false,
  },
  /*  
    FF_ID NUMERIC(18, 0),
  NR_FF VARCHAR(100),
  LA_DATA DATE,
  CLIENT VARCHAR(250),
  C_ID NUMERIC(18, 0),
  CFFID NUMERIC(18, 0),
  FE_ID INTEGER,
  FE_NR VARCHAR(100),

  VAL_TXT VARCHAR(23),
  VAL_TVA_TXT VARCHAR(23),
  TOTAL_TXT VARCHAR(23),
  INCASAT_TXT VARCHAR(23),
  REST_DE_INCASAT_TXT VARCHAR(23),  

  VAL_EUR_TXT VARCHAR(23),
  VAL_TVA_EUR_TXT VARCHAR(23),
  TOTAL_EUR_TXT VARCHAR(23),  
  INCASAT_EUR_TXT VARCHAR(23),
  REST_DE_INCASAT_EUR_TXT VARCHAR(23),
  
  VAL NUMERIC(18, 2),
  VAL_TVA NUMERIC(18, 2),
  TOTAL NUMERIC(18, 2),
  INCASAT NUMERIC(18, 2),
  REST_DE_INCASAT NUMERIC(18, 2),  
  
  MONEDA_FF VARCHAR(4),

  VAL_EUR NUMERIC(18, 2),
  VAL_TVA_EUR NUMERIC(18, 2),
  TOTAL_EUR NUMERIC(18, 2),  
  INCASAT_EUR NUMERIC(18, 2),
  REST_DE_INCASAT_EUR NUMERIC(18,2),
  
  
  NR_FE VARCHAR(100),
  ANULATA INTEGER,
  ARE_FLID INTEGER,
  DATA_INC DATE, --- PROBABIL ULTIMA INCASARE ---
  INCAS_FF_FARA_TVA NUMERIC(18, 2),
  INCAS_FF_TVA NUMERIC(18, 2),
  LOCATIE VARCHAR(1000),
  FISIER VARCHAR(500)
  */
  FF_ID: { ...invisibleID },
  NR_FF: {
    caption: "Numar factura",
    dataType: "string",
    // width: 100,//-- if first column importatnt not to flicker
  },
  LA_DATA: {
    caption: "Data factura",
    dataType: "date",
    format: undefined,
    width: 100,
    calculateCellValue: calcDateCellValue("LA_DATA"),
    calculateDisplayValue: calcDateCellValueDisplay("LA_DATA"),
    headerFilterDataSource: customizeDateFieldHeaderFilterData,
  },
  // CLIENT: {}
  // C_ID: { ...invisibleID },
  CFFID: { ...invisibleID },
  // FE_ID: { ...invisibleID },
  FE_NR: {
    caption: "Legata de proforma",
    dataType: "string",
    visible: false,
  },
  VAL_TXT: {
    caption: "Valoare fara TVA",
    dataType: "string",
    alignment: "right",
  },
  VAL_TVA_TXT: {
    caption: "Valoare TVA",
    dataType: "string",
    alignment: "right",
  },
  TOTAL_TXT: {
    caption: "Valoare cu TVA",
    dataType: "string",
    alignment: "right",
  },
  INCASAT_TXT: {
    caption: "Valoare incasata",
    dataType: "string",
    alignment: "right",
  },
  REST_DE_INCASAT_TXT: {
    caption: "Rest de incasat",
    dataType: "string",
    alignment: "right",
  },
  VAL_EUR_TXT: {
    caption: "Valoare fara TVA (conversie EUR)",
    dataType: "string",
    alignment: "right",
  },
  VAL_TVA_EUR_TXT: {
    caption: "Valoare TVA (conversie EUR)",
    dataType: "string",
    alignment: "right",
  },
  TOTAL_EUR_TXT: {
    caption: "Valoare cu TVA (conversie EUR)",
    dataType: "string",
    alignment: "right",
  },
  INCASAT_EUR_TXT: {
    caption: "Valoare incasata (conversie EUR)",
    dataType: "string",
    alignment: "right",
  },
  REST_DE_INCASAT_EUR_TXT: {
    caption: "Rest de incasat (conversie EUR)",
    dataType: "string",
    alignment: "right",
  },
  VAL: { ...invisibleID },
  VAL_TVA: { ...invisibleID },
  TOTAL: { ...invisibleID },
  INCASAT: { ...invisibleID },
  REST_DE_INCASAT: { ...invisibleID },
  MONEDA_FF: {
    caption: "Moneda facturii",
    dataType: "string",
    width: 100,
    alignment: "center",
    visible: false,
  },
  VAL_EUR: { ...invisibleID },
  VAL_TVA_EUR: { ...invisibleID },
  TOTAL_EUR: { ...invisibleID },
  INCASAT_EUR: { ...invisibleID },
  REST_DE_INCASAT_EUR: { ...invisibleID },
  NR_FE: { ...invisibleID },
  ANULATA: {
    //TODO: #52 boolean header filter and cell template
    caption: "Anulata",
    dataType: "boolean",
    width: 100,
    alignment: "center",
    visible: false,
  },

  ARE_FLID: { ...invisibleID },
  DATA_INC: {
    caption: "Ultima incasare",
    dataType: "date",
    format: undefined,
    width: 100,
    calculateCellValue: calcDateCellValue("DATA_INC"),
    calculateDisplayValue: calcDateCellValueDisplay("DATA_INC"),
    headerFilterDataSource: customizeDateFieldHeaderFilterData,
  },
  INCAS_FF_FARA_TVA: { ...invisibleID },
  INCAS_FF_TVA: { ...invisibleID },
  LOCATIE: { ...invisibleID },
  FISIER: { ...invisibleID },
};

/* ---------------------------- COMMON FUNCTIONS ---------------------------- */

export const DOMPurify = createDOMPurify(window);

export const SEARCH_MIN_CHARS_NUMBER = 3;

export const md = new Remarkable("full", {
  html: true,
  xhtmlOut: true,
  breaks: false,
  typographer: true,
});
md.inline.ruler.enable(["ins", "mark"]);
md.inline.ruler.disable(["text", "links", "htmltag", "autolink", "entity"]);
md.block.ruler.disable(["table", "footnote"]);
//
md.use(mention).use(mention_hsh);
//
const rgx = "?![^<>]*>";
//
const _parts = (x: string) =>
  compose(
    uniq,
    map((x: string) => trim(x)),
    filter((x: string) => size(x) > 2),
    // initially we just used: split(' '),
    // map by words but extract text between quotes
    (x: string) =>
      x.match(/".*?"|[^\s]+/g)?.map((s: string) => s.replace(/^"(.*)"$/, "$1")),
    toLower,
    trim
  )(x);
//
export const parts = curry(_parts);
//
const _regexParts = (partsArray: any) =>
  map(
    (x) =>
      new RegExp(
        `(${trim(x.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&"))})(${rgx})`,
        "gi"
      ),
    partsArray
  );
//
export const regexParts = curry(_regexParts);
//
const _mark = (x: any, y: any) =>
  reduce(
    (html2, regex) =>
      compose(
        reduce(
          (html, part) =>
            regex.test(part) ? `${html}<mark>${part}</mark>` : `${html}${part}`,
          ""
        ),
        split(regex)
      )(html2),
    x,
    y //regexParts
  );
//
export const mark = curry(_mark);
//
export const markRegexParts = (value: any) => {
  const parts = compose(
    uniq,
    map((x) => trim(x as string)),
    filter((x) => size(x) > 2),
    split(" "),
    toLower,
    trim
  )(value);

  const rgx = "?![^<>]*>";

  return map((x) => new RegExp(`(${trim(x)})(${rgx})`, "gi"), parts);
};
